// ─────────────────────────────────────────────────────────────────────────────
// REQ-098 — Piezas compartidas de las pantallas del Programa de Reconocimiento.
//
// FASE 3 (paridad demo) — H-28/H-29/H-30/H-32/H-34: este archivo dejó de proveer su propio shell.
// Providers (UserProvider/ThemeProvider), GradientBackground, LegalGate, MockDataBadge y el sidebar
// viven ahora UNA sola vez en <AppLayout/> (src/router/AppRouter.tsx), compartido con el portal.
// `RecognitionShell` solo aporta ya el contenedor visual de la maqueta (.home-page-root >
// .home-dashboard-area > .dashboard-root, ver demo HomePage.tsx : 405/463/464) y el tab bar móvil
// del módulo. `RecognitionNav` (nav horizontal 100% inline) SE ELIMINÓ — su función la cumple ahora
// el `.reco-submenu` del sidebar unificado (F2). `NotificationBell` se restiló con las clases
// `.side-notif-*` del demo y se mudó de render al `<Sidebar/>` (components/layout/Sidebar.tsx).
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { RECO_DETAIL_PARAM } from './RecognitionDetailOverlay';
import { useNavigationGuard } from '@/contexts/UnsavedChangesContext';
import { useUserContext } from '@/contexts/UserContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import {
  RecognitionApiError,
  useNotificationInbox,
  useMarkNotificationRead,
  useMarkNotificationUnread,
  useMarkAllNotificationsRead,
  useActiveCampaign,
} from '@/api/recognition';

/** Query param con el id de la campaña en un aviso de campaña (espejo de
 *  `RecognitionDeepLinks.CampaignParam` del backend). Distinto de `?reco` (reconocimiento). */
const CAMPAIGN_PARAM = 'campana';
import type { NotificationType } from '@/api/recognition';
import './recognition.css';

/** Muro genérico: el destino por DEFECTO que el backend graba cuando un aviso no fijó deep-link propio
 *  (`req.InAppLink ?? RecognitionDeepLinks.Brillamos`). */
const RECOGNITION_WALL = '/app/brillamos';

/**
 * Fallback de destino por TIPO de aviso (espejo de `RecognitionDeepLinks` del backend). El destino se
 * graba DENTRO de cada notificación al crearse, así que los avisos anteriores al ruteo por tipo quedaron
 * apuntando al muro genérico `/app/brillamos`; desplegar el backend no los reescribe. Cuando el `link`
 * guardado es ese muro genérico (o falta), se recalcula por tipo para llevarlos al menos a la SECCIÓN
 * correcta. Sin el id del item concreto (no viajaba en esos avisos viejos): la aprobación abre la COLA
 * (`?aprobacion=1`, sin expandir una campaña). Los avisos nuevos ya traen su deep-link específico (con
 * id) y NO pasan por aquí. `campaignUpdate`/`campaignEnding` se quedan en el muro a propósito (su destino
 * ES el muro de la campaña; sin id, el muro genérico es lo correcto).
 */
const FALLBACK_BY_TYPE: Record<string, string> = {
  badgeEarned: '/app/perfil',
  streakAndBadges: '/app/perfil',
  roleChanged: '/app/perfil',
  winner: '/app/perfil',
  wrapped: '/app/perfil',
  welcome: '/app',
  accountActivated: '/app',
  systemBroadcast: '/app',
  streakAtRisk: '/app/reconocer',
  dailyNudge: '/app/reconocer',
  moderationCaseAlert: '/app/admin/moderacion',
  moderationDigest: '/app/admin/moderacion',
  bulkUploadReminder: '/app/admin/gestion',
  lowParticipationAlert: '/app/admin/reporteria',
  winnerUploadReminder: '/app/admin/ganadores',
  campaignApproval: '/app/admin/campanas?aprobacion=1',
};

/** Destino efectivo de un aviso: respeta su deep-link propio; si es el muro genérico (o falta), lo
 *  recalcula por tipo. Así, los avisos creados antes del ruteo por tipo también aterrizan en su lugar. */
function effectiveLink(link: string | null, type: string): string | null {
  if (link && link !== RECOGNITION_WALL) return link;
  return FALLBACK_BY_TYPE[type] ?? link;
}

/**
 * Envuelve una pantalla de reconocimiento con el contenedor de layout de la maqueta
 * (paridad H-30: antes era un <main> con ancho máximo fijo puesto vía atributo style
 * inline). El shell real (providers, sidebar, LegalGate, fondo, MockDataBadge, y desde Fase 4 el
 * nav inferior móvil de rutas frontline) lo aporta `<AppLayout/>`.
 */
export function RecognitionShell({
  children,
  wide = false,
  className = '',
  backdrop,
}: Readonly<{ children: ReactNode; wide?: boolean; className?: string; backdrop?: ReactNode }>) {
  // `wide` replica el flag `embedded` del demo (ShineTogetherHomePage.tsx : 621:
  // `embedded ? 'shine-embedded-area' : 'home-dashboard-area'`). `.home-dashboard-area` reserva
  // 300px a la izquierda para el riel de pastillas de Fundamentales, que la landing de campaña no
  // muestra: usarlo ahí dejaba el contenido en 741px y recorrido a la derecha en vez de centrado
  // a 1080px como el demo.
  // `className` permite que una pantalla añada el wrapper de tema del prototipo sin duplicar el
  // armazón (Brillamos usa `shine-home shine-home--embedded`, demo ShineTogetherHomePage.tsx : 601).
  // `backdrop` se pinta como HERMANO del área de contenido, igual que el `<StarfieldCanvas/>` del
  // prototipo (demo : 602): así queda por debajo de las tarjetas y cubre el viewport entero.
  const rootClass = ['home-page-root', className].filter(Boolean).join(' ');
  return (
    <div className={rootClass}>
      {backdrop}
      <div className={wide ? 'shine-embedded-area' : 'home-dashboard-area'}>
        <div className="dashboard-root">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REQ-071/072/073 — Campana de notificaciones in-app (inbox persistente).
//
// El backend de inbox YA existe (GET v1/notifications/inbox, POST .../{id}/read, .../read-all sobre
// NotificationService). La campana queda tras el FEATURE FLAG VITE_FEATURE_RECOGNITION_INBOX (para
// activarla por entorno). Contador de no leídas + panel con banners por escenario, marcar-leído y
// deep-link (REQ-073): si el item trae `link`, al pulsarlo navega y marca como leído. Estados honestos
// (loading/error/empty) — el hook con retry 0 cae a error honesto si el servicio no responde.
//
// FASE 3 — restilada con las clases `.side-notif-*` del demo (CONTRATO-SIDEBAR.md §6, clases
// "portadas" en F2 pero sin consumidor hasta ahora) y renderizada dentro del `<Sidebar/>` en vez de
// la extinta nav horizontal. El demo no tiene JSX que use `.side-notif-*` (CSS "muerta" en la propia
// maqueta — ninguna pantalla del demo la consume), así que la estructura interna del panel es una
// composición razonable sobre esas clases, no una copia literal de JSX del demo. Ver MAPA-CLASES.md.
// ─────────────────────────────────────────────────────────────────────────────
export const RECOGNITION_INBOX_ENABLED: boolean =
  import.meta.env.VITE_FEATURE_RECOGNITION_INBOX === 'true';

/**
 * Ícono contextual por tipo espejo de notificación (RecognitionTemplateType.ToKey). Fallback a
 * fa-bell para tipos no mapeados (la unión de NotificationType es abierta).
 */
const NOTIF_ICON: Record<string, string> = {
  recognitionReceived: 'fa-star',
  streakAndBadges: 'fa-fire',
  badgeEarned: 'fa-medal',
  campaignUpdate: 'fa-hourglass-half',
  campaignApproval: 'fa-clipboard-check',
  inviteToRecognize: 'fa-hand-holding-heart',
  digest: 'fa-envelope-open-text',
};

function notifIcon(type: NotificationType): string {
  return NOTIF_ICON[type] ?? 'fa-bell';
}

/**
 * Diseño "Ver mi reconocimiento" — id del reconocimiento que un deep-link de detalle lleva en
 * `?reco=`, o null si el link no es de detalle. El clic principal de "reconocimiento recibido" trae
 * `/app/brillamos?reco={id}` o `/app/reconocer?reco={id}` (compuesto por el backend según programa).
 */
function recoIdFromLink(link: string): string | null {
  const q = link.indexOf('?');
  if (q < 0) return null;
  return new URLSearchParams(link.slice(q + 1)).get(RECO_DETAIL_PARAM);
}

/**
 * Id de la campaña de un aviso de CAMPAÑA (nueva/por terminar), que el backend pone en `?campana=`.
 * Null si el link no es de campaña. La campana lo usa para decidir si navegar al muro (campaña
 * activa) o mostrar el aviso de "ya finalizó".
 */
function campanaIdFromLink(link: string): string | null {
  const q = link.indexOf('?');
  if (q < 0) return null;
  return new URLSearchParams(link.slice(q + 1)).get(CAMPAIGN_PARAM);
}

/**
 * Campana in-app (REQ-071 / RF-164): contador de no leídas, panel con banners por escenario, marcar
 * leída/no leída, limpiar el badge, historial paginado y deep-link (REQ-073). Solo visible con el
 * feature flag VITE_FEATURE_RECOGNITION_INBOX.
 *
 * `variant` existe porque el riel lateral está OCULTO por debajo de 768 px: montada solo ahí, toda la
 * funcionalidad quedaba inalcanzable en móvil mientras el perfil seguía enseñando el contador de no
 * leídas, es decir, un número que el usuario no podía bajar. En `mobile` el disparador se dibuja como
 * una pestaña de la barra inferior y el panel se abre hacia arriba.
 */
export function NotificationBell({ variant = 'sidebar' }: Readonly<{ variant?: 'sidebar' | 'mobile' }> = {}) {
  const { t } = useTranslation('recognition');
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const guardNavigation = useNavigationGuard();
  const [open, setOpen] = useState(false);
  const panelRef = useClickOutside<HTMLDivElement>(() => setOpen(false));
  const enabled = RECOGNITION_INBOX_ENABLED;
  const inbox = useNotificationInbox(enabled);
  const markRead = useMarkNotificationRead();
  const markUnread = useMarkNotificationUnread();
  const markAll = useMarkAllNotificationsRead();
  const { demoUser } = useUserContext();
  // Campaña activa de la OU del usuario (una sola por OU; ver GetActiveByOuAsync). Sirve para decidir
  // si un aviso de campaña navega al muro (coincide) o avisa que ya finalizó (no coincide / 204).
  const activeCampaign = useActiveCampaign(enabled ? (demoUser.ou || undefined) : undefined);
  // Aviso "esta campaña ya finalizó" cuando se toca una notificación de una campaña que ya no está activa.
  const [campaignEnded, setCampaignEnded] = useState(false);

  if (!enabled) return null;

  // RF-164 — Historial: se concatenan las páginas cargadas, DEDUPLICANDO por id. La paginación es por
  // desplazamiento sobre una lista ordenada por fecha, así que un aviso que llega mientras el usuario
  // pagina empuja a los demás una posición y el último de una página reaparece en la siguiente: sin
  // deduplicar, React recibe dos hijos con la misma `key` y el aviso se pinta dos veces.
  const pages = inbox.data?.pages ?? [];
  const items = [...new Map(pages.flatMap((p) => p.items).map((n) => [n.id, n])).values()];

  // El contador se toma de la ÚLTIMA página descargada, no de la primera: cada página trae la cuenta
  // recalculada en SU momento de descarga, y la primera es la más vieja de la caché. Leyendo de ahí,
  // los avisos llegados entre una carga y otra no aparecían en el badge.
  const unread = pages[pages.length - 1]?.unreadCount ?? 0;

  function go(link: string) {
    setOpen(false);
    // Aviso de RECONOCIMIENTO (?reco): dentro de /app solo añadimos `?reco=` a la ubicación ACTUAL,
    // sin cambiar el pathname; `RecognitionDetailOverlay` abre el modal EN SITIO. El reconocimiento NO
    // navega a ninguna campaña ni al muro (el modal ya muestra su etiqueta de campaña).
    const reco = recoIdFromLink(link);
    if (reco && location.pathname.startsWith('/app/')) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(RECO_DETAIL_PARAM, reco);
        return next;
      });
      return;
    }

    // Aviso de CAMPAÑA (?campana): navega a la campaña SOLO si sigue activa para el usuario. Como el
    // backend garantiza una sola campaña activa por OU, basta comparar el id contra `useActiveCampaign`.
    const campana = campanaIdFromLink(link);
    if (campana) {
      const resolved = activeCampaign.isSuccess || activeCampaign.isError;
      if (!resolved || activeCampaign.data?.id === campana) {
        // Activa (o aún sin resolver → optimista): navega al muro Brillamos (abre la campaña).
        guardNavigation(() => navigate('/app/brillamos'));
      } else {
        // Resuelto y NO es la activa (finalizó / otro alcance) → aviso amigable, sin navegar.
        setCampaignEnded(true);
      }
      return;
    }

    // Deep-link interno (REQ-073): navega dentro de la SPA; externo abre en la misma pestaña. Fuera
    // de /app/* un link de detalle también cae aquí: al navegar, la zona monta el overlay al llegar.
    // Esta navegación SÍ desmonta la pantalla actual, así que pasa por la guarda de cambios sin
    // guardar: si hay un formulario sucio, se confirma antes (fuera del provider, ejecuta directo).
    guardNavigation(() => {
      if (link.startsWith('/')) navigate(link);
      else window.location.assign(link);
    });
  }

  function onItemClick(id: string, isRead: boolean, link: string | null, type: string) {
    if (!isRead) markRead.mutate(id);
    // El destino se graba al crear el aviso; si es el muro genérico (aviso anterior al ruteo por tipo)
    // se recalcula por TIPO para que igual aterrice en su sección.
    const dest = effectiveLink(link, type);
    if (dest) go(dest);
  }

  /**
   * RF-164 — Alterna el estado del aviso. Sin la vuelta atrás, abrir la campana y tocar un aviso por
   * error lo daba por atendido para siempre; con ella el usuario puede dejarlo pendiente.
   */
  function toggleRead(id: string, isRead: boolean) {
    if (isRead) markUnread.mutate(id);
    else markRead.mutate(id);
  }

  const isMobile = variant === 'mobile';

  return (
    <>
    <div className={`side-notif-wrap${isMobile ? ' side-notif-wrap--mobile' : ''}`} ref={panelRef}>
      <button
        type="button"
        // En el riel la campana ES una entrada más del menú: hereda `side-link` para tener la misma
        // píldora, el mismo hover, el mismo modo oscuro y —sobre todo— las mismas reglas de colapso
        // que el resto de los iconos (`.side-nav--collapsed .side-link`). Sin ese `side-link`,
        // `.side-notif-btn` se quedaba fuera de TODAS ellas: el rótulo seguía pintándose con el riel
        // plegado y, como `.side-nav-bottom` se encoge a su contenido cuando `.side-nav--collapsed`
        // pone `align-items: center`, el ancho del texto empujaba el pie entero fuera del riel
        // (idioma y tema incluidos). `side-notif-btn` se conserva solo como ancla del badge y del
        // panel.
        className={isMobile ? 'mbn-link side-notif-trigger--mobile' : 'side-link side-notif-btn'}
        aria-label={t('notifications.title')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <i className="fas fa-bell" aria-hidden="true" />
        {/* `side-link-label` (no una clase propia): es lo que el riel oculta al plegarse. */}
        <span className={isMobile ? undefined : 'side-link-label'}>{t('notifications.title')}</span>
        {unread > 0 && (
          <span className="side-notif-badge" aria-label={t('notifications.unread', { count: unread })}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className={`side-notif-panel${isMobile ? ' side-notif-panel--mobile' : ''}`}
          role="dialog"
          aria-label={t('notifications.title')}
        >
          <div className="side-notif-panel-header">
            <strong>{t('notifications.title')}</strong>
            <div className="side-notif-panel-header-actions">
              {unread > 0 && (
                <button type="button" className="side-notif-markall-btn" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
                  {t('notifications.markAllRead')}
                </button>
              )}
              <button
                type="button"
                className="side-notif-close-btn"
                onClick={() => setOpen(false)}
                aria-label={t('notifications.close')}
              >
                <i className="fas fa-xmark" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="side-notif-panel-list">
            {inbox.isLoading && <p className="side-notif-panel-empty">{t('common.loading')}</p>}
            {/* Estado honesto: si el servicio de inbox no responde, sin datos falsos. */}
            {inbox.isError && <p className="side-notif-panel-empty">{t('notifications.unavailable')}</p>}
            {!inbox.isLoading && !inbox.isError && items.length === 0 && (
              <p className="side-notif-panel-empty">{t('notifications.empty')}</p>
            )}

            {items.map((n) => (
              <div key={n.id} className={`side-notif-item${n.isRead ? '' : ' side-notif-item--unread'}`}>
                <button
                  type="button"
                  className="side-notif-item-main"
                  onClick={() => onItemClick(n.id, n.isRead, n.link, n.type)}
                >
                  <span className="side-notif-item-icon"><i className={`fas ${notifIcon(n.type)}`} aria-hidden="true" /></span>
                  <span className="side-notif-item-content">
                    <p>{n.title}</p>
                    <p>{n.body}</p>
                    {n.actorName && <span className="side-notif-item-time">{n.actorName}</span>}
                  </span>
                </button>

                {/* RF-164 — Alternar leída/no leída. Es `role="switch"` porque comunica un estado
                    con dos valores, no una acción suelta: un lector de pantalla anuncia si el aviso
                    está leído sin tener que deducirlo del texto del botón. */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={n.isRead}
                  className="side-notif-item-readtoggle"
                  // El nombre designa lo que se controla —el aviso— y el estado lo lleva
                  // aria-checked. Con el nombre puesto en la acción, un lector anunciaba "marcar como
                  // no leída, activado", que se contradice; y 20 avisos daban 20 controles idénticos.
                  aria-label={`${t('notifications.readState')} — ${n.title}`}
                  title={n.isRead ? t('notifications.markUnread') : t('notifications.markRead')}
                  onClick={() => toggleRead(n.id, n.isRead)}
                >
                  {/* Convención de correo: sobre CERRADO = sin abrir (no leída), sobre ABIERTO = ya
                      leída. El estado real lo comunica aria-checked; el icono solo lo acompaña. */}
                  <i className={`fas ${n.isRead ? 'fa-envelope-open' : 'fa-envelope'}`} aria-hidden="true" />
                </button>
              </div>
            ))}

            {/* RF-164 — Historial completo. Sin esto solo se veían los 20 últimos avisos y no había
                forma de llegar a lo anterior. */}
            {inbox.hasNextPage && (
              <button
                type="button"
                className="side-notif-more-btn"
                onClick={() => inbox.fetchNextPage()}
                disabled={inbox.isFetchingNextPage}
              >
                {inbox.isFetchingNextPage ? t('common.loading') : t('notifications.loadMore')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
    {campaignEnded && createPortal(
      <div className="modal-overlay postit-modal-overlay" role="dialog" aria-modal="true" onClick={() => setCampaignEnded(false)}>
        <div className="postit-modal-card" onClick={(e) => e.stopPropagation()}>
          <button className="postit-modal-close" onClick={() => setCampaignEnded(false)} aria-label={t('common.close')}>
            <i className="fas fa-times" />
          </button>
          <p className="postit-modal-message">{t('notifications.campaignUnavailable')}</p>
        </div>
      </div>,
      document.body,
    )}
    </>
  );
}

/** Encabezado estándar de una pantalla — paridad H-32: `.dash-hero-title`/`.dash-hero-subtitle`
    del demo (HomePage.tsx : 475-480), sin estilos inline. */
export function RecognitionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <header className="rec-header">
      <h1 className="dash-hero-title">{title}</h1>
      {subtitle && <p className="dash-hero-subtitle">{subtitle}</p>}
    </header>
  );
}

interface StateBlockProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  emptyText?: string;
  onRetry?: () => void;
  children: ReactNode;
}

/**
 * Renderiza el estado real de una query: loading → error → empty → contenido.
 * Mensaje específico cuando el error es por servicio no configurado.
 * FASE 3 (H-32): estados sin estilos inline — `.rec-state` (+ modificadores). El bloque de error
 * (antes `background: rgba(225,31,38,0.08)` inline) es la captura reportada por el equipo; ahora
 * se ve igual pero vía clase.
 */
export function StateBlock({ isLoading, isError, error, isEmpty, emptyText, onRetry, children }: StateBlockProps) {
  const { t } = useTranslation('recognition');

  if (isLoading) {
    return <p role="status" className="rec-state rec-state--loading">{t('common.loading')}</p>;
  }

  if (isError) {
    // Causa real del fallo: servicio no configurado (status 0), sin permisos (403) o error genérico.
    // El `status` se lee por duck-typing para cubrir tanto RecognitionApiError como ApiError (Pulso).
    const status = typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: number }).status
      : undefined;
    const notConfigured = error instanceof RecognitionApiError && error.status === 0;
    const forbidden = status === 403;
    let message = t('common.error');
    if (notConfigured) message = t('common.notConfigured');
    else if (forbidden) message = t('common.forbidden');
    return (
      <div role="alert" className="rec-state rec-state--error">
        <p className="rec-state-text">{message}</p>
        {/* Reintentar no ayuda ante un 403 (falta de permisos), así que se oculta en ese caso. */}
        {onRetry && !forbidden && (
          <button type="button" className="rec-state-retry" onClick={onRetry}>
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }

  if (isEmpty) {
    return <p className="rec-state rec-state--empty">{emptyText ?? t('common.empty')}</p>;
  }

  return <>{children}</>;
}
